'use client'

import { featureFlagsAtom } from '@polaris/core/stores'
import { useAtomValue } from 'jotai'
import type { HTMLMotionProps } from 'motion/react'
import { motion } from 'motion/react'
import { useEffect, useState } from 'react'
import { GoPrivateToggle } from '@/src/components/privacy/go-private-toggle'
import {
  useNetworkSelection,
  useTokenSelection,
} from '@/src/stores/token-selection/actions'
import { FRAMER_MOTION_TOKEN_SELECT_EASING } from '@/src/utils/consts'
import { cn } from '@/src/utils/styles'
import { useModalMode } from '../modal-mode-context'
import { RouteOverview } from '../route-overview'
import { TokenSelection } from '../token-selection'
import { NetworkSelection } from '../token-selection/network/select'
import { AmountInErrors } from './amount-in-errors'
import { AmountOutErrors } from './amount-out-errors'
import { AmountOutUSDLabel } from './amount-out-usd-label'
import { CTA } from './cta'
import { DestinationAccountSelect } from './destination-account-select'
import { FromAmountUSDLabel } from './from-amount-usd-label'
import { FromBalanceSelectPopover } from './from-balance-select'
import { FromSection } from './from-section'
import { MaintenanceBanner } from './maintenance-banner'
import { NetworkSelector } from './network-selector'
import { PendingTradeBanner } from './pending-trade-banner'
import { RoutePreviewErrors } from './route-preview-errors'
import { SubmitButton } from './submit-button'
import { SwitchSwapButton } from './switch-button'
import { ToSection } from './to-section'
import { WalletBalance } from './wallet-balance'

interface SwapFormProps extends HTMLMotionProps<'form'> {}

/**
 * Reusable swap widget, it allows users to perform a swap from one token to another
 */
export const SwapForm = ({ className, ...rest }: SwapFormProps) => {
  const modal = useModalMode()
  const { isOpen: isTokenSelectionOpen, isAnimatedOpen } = useTokenSelection()

  const { isOpen: isNetworkSelectionOpen, close: closeNetworkSelection } =
    useNetworkSelection()

  const isOpen =
    isTokenSelectionOpen || isNetworkSelectionOpen || isAnimatedOpen

  const [delayedIsOpen, setDelayedIsOpen] = useState(isOpen)

  /**
   * Delayer for `absolute` class usage when closing
   * to avoid layout shifts
   */
  useEffect(() => {
    if (isOpen) {
      setDelayedIsOpen(true) // open: apply immediately
    } else {
      const timeout = setTimeout(() => {
        setDelayedIsOpen(false) // close: apply after delay
      }, 300)

      return () => clearTimeout(timeout)
    }
  }, [isOpen])

  return (
    <div
      className={cn({
        'absolute min-w-96': delayedIsOpen && !modal,
      })}
    >
      <div className='relative overflow-hidden rounded-3xl'>
        <motion.form
          className={cn(
            'bg-surface1-100 rounded-3xl relative overflow-hidden min-h-[448px] max-h-[768px]',
            {
              'max-w-96 !bg-background': isTokenSelectionOpen,
            },
            className,
          )}
          transition={{
            duration: 0.3,
            ease: FRAMER_MOTION_TOKEN_SELECT_EASING,
          }}
          animate={{ height: isOpen ? 'calc(100vh - 200px)' : 'auto' }}
          initial={{ height: 448 }}
          {...rest}
        >
          <div
            className={cn('transition-all duration-300 ease-default', {
              'scale-90 opacity-40': isOpen,
            })}
          >
            <SwapFormItems />
          </div>
        </motion.form>
        <TokenSelection />
        <NetworkSelection
          isOpen={isNetworkSelectionOpen}
          onRequestClose={closeNetworkSelection}
        />
        <FromBalanceSelectPopover />
        <DestinationAccountSelect />
      </div>
    </div>
  )
}

function SwapFormItems() {
  return (
    <div className='relative flex flex-col min-w-[384px]'>
      <div className='flex flex-col min-h-48 py-4'>
        <div className='flex items-center px-6 justify-between gap-3 min-h-8'>
          <AmountInErrors>
            <label
              className='text-secondary text-sm py-[6px]'
              htmlFor='fromAmount'
            >
              From
            </label>
          </AmountInErrors>
          <WalletBalance direction='from' />
        </div>

        <FromSection />

        <div className='flex items-start gap-3 w-full px-6 py-1 justify-between min-h-8'>
          <FromAmountUSDLabel />
          <NetworkSelector direction='from' />
        </div>
      </div>
      <div className='relative w-full h-px bg-border1 flex justify-center items-center'>
        <SwitchSwapButton />
      </div>
      <div className='flex items-center place-content-between gap-3 px-5 mt-4 min-h-8'>
        <AmountOutErrors>
          <p className='text-secondary text-sm py-1.5'>To</p>
        </AmountOutErrors>
        <WalletBalance direction='to' />
      </div>

      <ToSection />

      <div className='flex items-center gap-3 w-full px-5 justify-between min-h-8 mb-4'>
        <AmountOutUSDLabel />
        <NetworkSelector direction='to' />
      </div>

      <div className='px-5'>
        <PendingTradeBanner />
      </div>

      <footer className='px-5 pt-1 pb-4'>
        <CTA />
        {useAtomValue(featureFlagsAtom).privacyUi && <GoPrivateToggle />}
        <SubmitButton />
      </footer>

      <MaintenanceBanner />

      <RoutePreviewErrors>
        <RouteOverview />
      </RoutePreviewErrors>
    </div>
  )
}
